import React, { useState } from 'react';
import { Database, ArrowRight, MessageCircle, Zap, Shield } from 'lucide-react';

const ConstructionRAGDemo = () => {
  const [query, setQuery] = useState('');
  const [step, setStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [portal, setPortal] = useState('broker');

  // Sample construction insurance documents in vector database
  const documents = [
    { 
      id: 1, 
      title: "Commercial General Liability Policy - Construction", 
      content: "Coverage includes bodily injury, property damage, and personal injury claims arising from construction operations. Aggregate limit $2M per occurrence, $6M annual aggregate.", 
      relevance: 0,
      access: 'both'
    },
    { 
      id: 2, 
      title: "Builder's Risk Insurance Guidelines", 
      content: "Covers materials, supplies, and equipment at job sites during construction. Includes coverage for theft, vandalism, fire, and weather damage up to $5M per project.", 
      relevance: 0,
      access: 'both'
    },
    { 
      id: 3, 
      title: "Commission Structure - Construction Lines", 
      content: "Broker commission rates: General Liability 12%, Workers Comp 8%, Builder's Risk 15%, Equipment Coverage 10%. Performance bonuses available for loss ratio improvements.", 
      relevance: 0,
      access: 'broker'
    },
    { 
      id: 4, 
      title: "Claims History Dashboard - Project Alpha", 
      content: "Project Alpha claims: 3 minor injuries ($15K total), 1 equipment breakdown ($25K), weather delay coverage utilized ($50K). Overall loss ratio: 0.65", 
      relevance: 0,
      access: 'client'
    },
    { 
      id: 5, 
      title: "Workers' Compensation Rates by Trade", 
      content: "Construction trade rates per $100 payroll: Roofers $8.50, Electricians $3.20, General laborers $5.75, Crane operators $12.80, Demolition workers $15.20.", 
      relevance: 0,
      access: 'broker'
    },
    {
      id: 6,
      title: "Certificate of Insurance Template",
      content: "Standard certificate template for construction projects. Includes general liability, workers comp, and builder's risk. Auto-populated with current policy limits and expiration dates.",
      relevance: 0,
      access: 'client'
    }
  ];

  const sampleQueries = {
    broker: [
      "What's my commission rate on workers comp policies?",
      "Show me loss ratios for my construction accounts",
      "Which clients need coverage reviews this quarter?",
      "What are the current rates for crane operators?",
      "Find accounts with upcoming renewal dates"
    ],
    client: [
      "Generate a certificate of insurance for my subcontractor",
      "Show me our claims history for Project Alpha",
      "What's covered under our general liability policy?",
      "Is equipment breakdown covered during transport?",
      "What's our current loss ratio and how does it affect renewals?"
    ]
  };

  const [retrievedDocs, setRetrievedDocs] = useState([]);
  const [finalResponse, setFinalResponse] = useState('');

  const calculateRelevance = (query, doc) => {
    // Filter documents based on portal access
    if ((portal === 'broker' && doc.access === 'client') || (portal === 'client' && doc.access === 'broker')) {
      return 0; // No access to this document
    }

    const queryTerms = query.toLowerCase().split(' ');
    const docText = (doc.title + ' ' + doc.content).toLowerCase();
    
    let matches = 0;
    queryTerms.forEach(term => {
      if (docText.includes(term)) matches++;
      // Portal-specific matching
      if (portal === 'broker') {
        if (term === 'commission' && docText.includes('broker')) matches += 3;
        if (term === 'loss' && docText.includes('ratio')) matches += 2;
        if (term === 'renewal' && docText.includes('account')) matches += 2;
        if (term === 'rates' && docText.includes('payroll')) matches += 2;
      }
      if (portal === 'client') {
        if (term === 'certificate' && docText.includes('template')) matches += 3;
        if (term === 'claims' && docText.includes('history')) matches += 2;
        if (term === 'project' && docText.includes('alpha')) matches += 2;
      }
      // General insurance terms
      if (term === 'liability' && docText.includes('bodily injury')) matches += 2;
      if (term === 'equipment' && docText.includes('breakdown')) matches += 2;
      if (term === 'workers' && docText.includes('compensation')) matches += 2;
    });
    
    return Math.min((matches / queryTerms.length) * 100, 95);
  };

  const processQuery = async () => {
    if (!query.trim()) return;
    
    setIsProcessing(true);
    setStep(1);

    // Step 1: Convert query to vector
    await new Promise(resolve => setTimeout(resolve, 1500));
    setStep(2);

    // Step 2: Search vector database
    await new Promise(resolve => setTimeout(resolve, 1500));
    const accessibleDocs = documents.filter(doc => doc.access === 'both' || doc.access === portal);
    const docsWithRelevance = accessibleDocs.map(doc => ({
      ...doc,
      relevance: calculateRelevance(query, doc)
    })).sort((a, b) => b.relevance - a.relevance);
    
    setRetrievedDocs(docsWithRelevance.slice(0, 3));
    setStep(3);

    // Step 3: Generate response
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const topDoc = docsWithRelevance[0];
    let response = '';
    
    if (portal === 'broker') {
      if (query.toLowerCase().includes('commission')) {
        response = "Your commission structure for construction lines: General Liability 12%, Workers Comp 8%, Builder's Risk 15%, Equipment Coverage 10%. Performance bonuses are available for accounts that maintain good loss ratios.";
      } else if (query.toLowerCase().includes('loss') && query.toLowerCase().includes('ratio')) {
        response = "Based on your account portfolio, Project Alpha shows a 0.65 loss ratio, which is favorable for renewal discussions and demonstrates good risk management.";
      } else if (query.toLowerCase().includes('renewal')) {
        response = "Your construction accounts with upcoming renewals include several builder's risk policies and workers compensation programs. Focus on accounts with good loss experience for competitive renewal pricing.";
      } else if (query.toLowerCase().includes('rates')) {
        response = "Current workers comp rates per $100 payroll: Roofers $8.50, Electricians $3.20, General laborers $5.75, Crane operators $12.80, Demolition workers $15.20.";
      } else {
        response = `Broker Dashboard: ${topDoc?.content || 'No relevant broker information found for this query.'}`;
      }
    } else {
      if (query.toLowerCase().includes('certificate')) {
        response = "I can generate a certificate of insurance using our standard construction template. It will include your general liability, workers comp, and builder's risk coverage with current policy limits and expiration dates.";
      } else if (query.toLowerCase().includes('claims') && query.toLowerCase().includes('history')) {
        response = "Your Project Alpha claims history: 3 minor injuries totaling $15K, 1 equipment breakdown at $25K, and weather delay coverage utilized for $50K. Your overall loss ratio is 0.65, which is favorable for renewals.";
      } else if (query.toLowerCase().includes('liability') || query.toLowerCase().includes('general')) {
        response = "Your Commercial General Liability policy covers bodily injury, property damage, and personal injury claims from construction operations. You have $2M coverage per occurrence with a $6M annual aggregate limit.";
      } else if (query.toLowerCase().includes('equipment') || query.toLowerCase().includes('breakdown')) {
        response = "Equipment coverage includes breakdown protection during transport for owned and rented construction equipment including excavators, cranes, and specialized tools.";
      } else {
        response = `Client Dashboard: ${topDoc?.content || 'No relevant coverage information found for this query.'}`;
      }
    }
    
    setFinalResponse(response);
    setStep(4);
    setIsProcessing(false);
  };

  const resetDemo = () => {
    setQuery('');
    setStep(0);
    setRetrievedDocs([]);
    setFinalResponse('');
    setIsProcessing(false);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gradient-to-br from-blue-50 to-orange-100 min-h-screen">
      <div className="bg-white rounded-xl shadow-lg p-8">
        <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">RAG-Powered Insurance Dashboards</h1>
        <p className="text-center text-gray-600 mb-8">See how AI serves different information to broker and client portals</p>

        {/* Portal Selection & Query Input */}
        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <div className="flex justify-center mb-6">
            <div className="bg-white rounded-lg p-1 flex gap-1 border border-gray-300">
              <button
                onClick={() => setPortal('broker')}
                className={`px-6 py-3 rounded-md transition-all font-semibold ${
                  portal === 'broker' 
                    ? 'bg-blue-500 text-white shadow-md' 
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                🏢 Broker Portal
              </button>
              <button
                onClick={() => setPortal('client')}
                className={`px-6 py-3 rounded-md transition-all font-semibold ${
                  portal === 'client' 
                    ? 'bg-orange-500 text-white shadow-md' 
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                👷 Client Portal
              </button>
            </div>
          </div>

          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            {portal === 'broker' ? 'Broker Dashboard Query' : 'Client Dashboard Query'}
          </h2>
          
          <div className="flex gap-3 mb-4">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={portal === 'broker' ? 'Ask about commissions, renewals, loss ratios...' : 'Ask about your coverage, claims, certificates...'}
              className="flex-1 p-3 border border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none"
              onKeyPress={(e) => e.key === 'Enter' && processQuery()}
            />
            <button
              onClick={processQuery}
              disabled={!query.trim() || isProcessing}
              className={`px-6 py-3 text-white rounded-lg transition-colors disabled:bg-gray-400 ${
                portal === 'broker' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-orange-500 hover:bg-orange-600'
              }`}
            >
              {isProcessing ? 'Processing...' : 'Search'}
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-gray-600">
              {portal === 'broker' ? 'Broker queries:' : 'Client queries:'}
            </span>
            {sampleQueries[portal].map((sampleQuery, index) => (
              <button
                key={index}
                onClick={() => setQuery(sampleQuery)}
                className={`text-sm px-3 py-1 rounded-full transition-colors ${
                  portal === 'broker' 
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                    : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                }`}
              >
                {sampleQuery}
              </button>
            ))}
          </div>
        </div>

        {/* RAG Process Visualization */}
        {step > 0 && (
          <div className="space-y-6">
            {/* Step 1: Query Vectorization */}
            <div className={`border-2 rounded-lg p-6 transition-all duration-500 ${
              step >= 1 ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-green-100 rounded-full">
                  <Zap className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold">Step 1: Convert Query to Vector</h3>
                {step === 1 && <div className="animate-spin w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full"></div>}
              </div>
              <p className="text-gray-700">
                <strong>{portal === 'broker' ? 'Broker' : 'Client'} Query:</strong> "{query}"<br />
                <strong>AI Process:</strong> Converting natural language to mathematical representation<br />
                <strong>Vector Output:</strong> [0.23, 0.81, 0.45, 0.67, ...] (384-dimensional {portal} context vector)
              </p>
            </div>

            {/* Step 2: Vector Search */}
            <div className={`border-2 rounded-lg p-6 transition-all duration-500 ${
              step >= 2 ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-100 rounded-full">
                  <Database className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold">Step 2: Search {portal === 'broker' ? 'Broker' : 'Client'} Database</h3>
                {step === 2 && <div className="animate-spin w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full"></div>}
              </div>
              <p className="text-gray-700 mb-4">
                Searching {portal}-accessible documents with role-based permissions...
              </p>
              
              {step >= 2 && (
                <div className="space-y-3">
                  <h4 className="font-medium">Retrieved Documents (by relevance):</h4>
                  {retrievedDocs.map((doc, index) => (
                    <div key={doc.id} className="bg-white p-4 rounded border border-gray-200">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Shield className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-blue-800">{doc.title}</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              doc.access === 'broker' ? 'bg-blue-100 text-blue-700' :
                              doc.access === 'client' ? 'bg-orange-100 text-orange-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {doc.access === 'both' ? 'Shared Access' : `${doc.access} Only`}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">{doc.content}</p>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-sm font-semibold text-blue-700">{doc.relevance.toFixed(1)}%</div>
                          <div className="w-16 bg-gray-200 rounded-full h-2 mt-1">
                            <div 
                              className="bg-gradient-to-r from-orange-400 to-red-500 h-2 rounded-full transition-all"
                              style={{ width: `${doc.relevance}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Step 3: Augmented Generation */}
            <div className={`border-2 rounded-lg p-6 transition-all duration-500 ${
              step >= 3 ? 'border-purple-400 bg-purple-50' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-100 rounded-full">
                  <ArrowRight className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold">Step 3: Create Portal-Specific Prompt</h3>
                {step === 3 && <div className="animate-spin w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full"></div>}
              </div>
              <div className="bg-white p-4 rounded border border-gray-200">
                <p className="text-sm text-gray-600 mb-2"><strong>Enhanced Prompt to Insurance AI:</strong></p>
                <div className="bg-gray-100 p-4 rounded text-sm font-mono">
                  <div className="text-blue-600 mb-2">CONTEXT:</div>
                  {retrievedDocs[0]?.content || "Retrieved relevant documents..."}<br /><br />
                  
                  <div className="text-green-600 mb-2">{portal.toUpperCase()} QUERY:</div>
                  {query}<br /><br />
                  
                  <div className="text-purple-600 mb-2">PORTAL ACCESS:</div>
                  User accessing {portal} portal - provide {portal}-appropriate response<br /><br />
                  
                  <div className="text-orange-600 mb-2">INSTRUCTIONS:</div>
                  Answer with {portal}-specific context and access-appropriate information.
                </div>
              </div>
            </div>

            {/* Step 4: Final Response */}
            {step >= 4 && (
              <div className="border-2 border-emerald-400 bg-emerald-50 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-emerald-100 rounded-full">
                    <MessageCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold">
                    {portal === 'broker' ? 'Broker Dashboard Response' : 'Client Dashboard Response'}
                  </h3>
                </div>
                <div className="bg-white p-6 rounded border border-emerald-200">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${portal === 'broker' ? 'bg-blue-100' : 'bg-orange-100'}`}>
                      {portal === 'broker' ? '🏢' : '👷'}
                    </div>
                    <div>
                      <p className="text-gray-800 mb-3">{finalResponse}</p>
                      <div className="text-sm text-emerald-700 bg-emerald-100 p-2 rounded">
                        <strong>Source:</strong> {retrievedDocs[0]?.title || "Dashboard database"}
                        <span className="ml-2 text-xs">
                          ({portal} portal access)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Reset Button */}
        {step > 0 && (
          <div className="text-center mt-8">
            <button
              onClick={resetDemo}
              className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Try Another Query
            </button>
          </div>
        )}

        {/* Dashboard Benefits */}
        <div className="mt-8 bg-gradient-to-r from-cyan-50 to-blue-100 border border-cyan-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-cyan-800 mb-3">🏗️ Dashboard Benefits for Construction Insurance</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                🏢 Broker Portal Benefits
              </h4>
              <ul className="text-sm text-cyan-600 space-y-1">
                <li>• Instant access to commission structures and rates</li>
                <li>• Real-time loss ratio tracking across accounts</li>
                <li>• Automated renewal reminders and opportunities</li>
                <li>• Quick underwriting appetite lookups</li>
                <li>• Performance analytics for portfolio management</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-orange-700 mb-3 flex items-center gap-2">
                👷 Client Portal Benefits
              </h4>
              <ul className="text-sm text-cyan-600 space-y-1">
                <li>• Instant coverage verification and limits</li>
                <li>• Real-time claims status and history</li>
                <li>• Certificate of insurance generation</li>
                <li>• Project-specific coverage explanations</li>
                <li>• Safety training resources and requirements</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Real Dashboard Applications */}
        <div className="mt-6 bg-orange-50 border border-orange-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-orange-800 mb-3">🎯 Real Dashboard Applications</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg border border-orange-200">
              <h4 className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                🏢 Broker Portal Use Cases
              </h4>
              <ul className="text-sm text-orange-600 space-y-1">
                <li>• "Show me all accounts with loss ratios above 0.8"</li>
                <li>• "Which construction clients need renewal attention?"</li>
                <li>• "What's my YTD commission on workers comp?"</li>
                <li>• "Find similar accounts for competitive benchmarking"</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-lg border border-orange-200">
              <h4 className="font-semibold text-orange-700 mb-2 flex items-center gap-2">
                👷 Client Portal Use Cases
              </h4>
              <ul className="text-sm text-orange-600 space-y-1">
                <li>• "Generate certificate for ABC Subcontractor"</li>
                <li>• "What's our deductible for equipment claims?"</li>
                <li>• "Show safety requirements for high-rise work"</li>
                <li>• "Track status of last week's injury claim"</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Portal Access & Security */}
        <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-purple-800 mb-3">🔒 Smart Access Control</h3>
          <p className="text-purple-700 mb-4">
            RAG systems automatically filter information based on user permissions and portal access levels.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded border border-purple-200">
              <h4 className="font-semibold text-blue-700 mb-2">🏢 Broker-Only Information</h4>
              <ul className="text-sm text-purple-600 space-y-1">
                <li>• Commission rates and structures</li>
                <li>• Underwriting guidelines and appetite</li>
                <li>• Profit margins and carrier relationships</li>
                <li>• Competitive pricing strategies</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded border border-purple-200">
              <h4 className="font-semibold text-orange-700 mb-2">👷 Client-Only Information</h4>
              <ul className="text-sm text-purple-600 space-y-1">
                <li>• Specific project claims history</li>
                <li>• Individual experience modifiers</li>
                <li>• Company-specific safety violations</li>
                <li>• Confidential project details</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Dashboard Performance Metrics */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
            <div className="text-2xl font-bold text-blue-700 mb-1">3 sec</div>
            <div className="text-sm text-blue-600">Average dashboard query response</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
            <div className="text-2xl font-bold text-green-700 mb-1">94%</div>
            <div className="text-sm text-green-600">User satisfaction with AI responses</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 text-center">
            <div className="text-2xl font-bold text-purple-700 mb-1">60%</div>
            <div className="text-sm text-purple-600">Reduction in support tickets</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConstructionRAGDemo;
